import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { filter, first, startWith } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { CCAuthService, CCPicklistService, GoogleOAuthService } from '@maids/cc-erp-services';
import { CacheService } from '../../services/cache.service';
import { environment } from 'src/environments/environment';
import { assets } from '@maids/cc-lib/utils';
import { trigger, style, animate, transition } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { CCInputComponent } from '@maids/cc-lib/input';

@Component({
  selector: 'app-new-login',
  templateUrl: './new-login.component.html',
  styleUrls: ['./new-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(1000, style({ opacity: 1 })),
      ]),
      transition(':leave', [animate(1000, style({ opacity: 0 }))]),
    ]),
  ],
})
export class NewLoginComponent implements OnInit, OnDestroy {
  quotes: string[] = [];
  currentIndex: number = 0;
  currentQuote: string = '';
  showQuoteTime: number = 40000; // 40 seconds

  smileyDecimal: string = '🙂';
  
  quoteAnimationState = '';
  isLandscape: boolean = false;
  isResponsiveLandscape: boolean = false;

  googleConfig = {
    scope: 'openid email',
    redirectUri: environment.googleRedirectUri,
    state: {
      app: 'new-erp',
      host: window.location.origin,
    },
  };

  constructor(
    private _auth: CCAuthService,
    private _router: Router,
    private _picklistService: CCPicklistService,
    private _cdr: ChangeDetectorRef,
    private _googleOAuth: GoogleOAuthService,
    @Inject(DOCUMENT) private document: Document,
    private _route: ActivatedRoute,
    private _cacheService: CacheService
  ) {}

  private readonly _currentNavigation = this._router.getCurrentNavigation();
  private readonly _subscriptions = new Subscription();

  ngOnInit(): void {
    this.getQuotes();
    this.checkScreenOrientation();
    // Check if this is a Google OAuth callback
    if (
      this._route.snapshot.queryParams['code'] &&
      this._route.snapshot.queryParams['state']
    ) {
      this.handleGoogleOAuthCallback();
    }
  }

  getQuotes() {
    this._picklistService
      .getPicklist({
        code: 'login_quotes',
      })
      .subscribe((res) => {
        this.quotes = res.map(
          (item: { label: string; id: number }) => item.label
        );
        this.iterateOverQuotes();
      });
  }

  iterateOverQuotes() {
    this._subscriptions.add(
      interval(this.showQuoteTime)
        .pipe(startWith(0))
        .subscribe(() => {
          this.currentIndex = (this.currentIndex + 1) % this.quotes.length;
          this.currentQuote = '';
          this._cdr.detectChanges();
          setTimeout(() => {
            this.currentQuote = this.quotes[this.currentIndex];
            this._cdr.detectChanges();
          }, 2000);
        })
    );
  }

  imageUrl(path: string) {
    return environment.production && !environment.newErp
      ? assets.getBasePath() + `new-theme-pages/assets/images/${path}`
      : assets.getBasePath() + `assets/images/${path}`;
  }

  clearAllCookies() {
    const cookies = this.document.cookie.split(';');
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      this.document.cookie =
        name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }



  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenOrientation();
  }

  private checkScreenOrientation() {
    this.isLandscape =
      window.innerWidth > window.innerHeight && window.innerWidth < 880;
    this.isResponsiveLandscape =
      window.innerWidth > window.innerHeight &&
      window.innerWidth > 880 &&
      window.innerWidth < 1400;
  }

  /**
   * Start Google OAuth authentication
   */
  startGoogleAuth(): void {
    this.onGoogleAuthStart();
    this._googleOAuth.startAuthorizationCodeFlow(this.googleConfig);
  }

  /**
   * Handle Google OAuth callback on page load
   */
  private handleGoogleOAuthCallback(): void {
    this._subscriptions.add(
      this._googleOAuth.handleOAuthCallback().subscribe({
        next: (user) => {
          if (user) {
            this._cacheService.clearERPMenuCache();
            this.onGoogleAuthSuccess(user);
          }
        },
        error: (error) => this.onGoogleAuthError(error),
      })
    );
  }

  /**
   * Handle successful Google authentication
   */
  async onGoogleAuthSuccess(user: any): Promise<void> {
    const redirect = this._currentNavigation?.extras.state?.['redirectTo'];
    this._router.navigate([redirect ?? '/']);
  }

  /**
   * Handle Google authentication error
   */
  onGoogleAuthError(error: any): void {
    console.error('Google authentication error:', error);

    // Handle different error types with user-friendly messages
    if (error.includes?.('popup_blocked')) {
      console.warn('Popup was blocked for Google sign-in');
    } else if (error.includes?.('access_denied')) {
      console.warn('Google sign-in was cancelled');
    } else {
      console.warn('Google authentication failed. Please try again.');
    }

    // Clear any cookies on auth failure
    this.clearAllCookies();
  }

  /**
   * Handle Google authentication start
   */
  onGoogleAuthStart(): void {
    console.log('Google authentication started');
  }
}